import { useState } from "react";
import { Loader2, X, Upload, LayoutGrid } from "lucide-react";

/**
 * Omni Reference multi-image/video upload card. Shared across all generation modes.
 * `testIdSuffix` differentiates the pipeline copy so tests can target either surface.
 * State (omniRefs, add/remove) is owned by the parent so both card instances share the same refs list.
 * Supports drag & drop of images + videos onto the drop zone.
 */
export function OmniReferenceCard({
  mode,
  omniRefs,
  addOmniRef,
  removeOmniRef,
  setOmniRefs,
  testIdSuffix = "",
}) {
  const [dragOver, setDragOver] = useState(false);
  const inputId = `omni-ref-input${testIdSuffix ? "-" + testIdSuffix : ""}`;
  const pickBtnId = `omni-ref-pick-btn${testIdSuffix ? "-" + testIdSuffix : ""}`;
  const gridId = `omni-ref-grid${testIdSuffix ? "-" + testIdSuffix : ""}`;
  const dropZoneId = `omni-ref-dropzone${testIdSuffix ? "-" + testIdSuffix : ""}`;
  const thumbPrefix = testIdSuffix === "pipeline" ? "omni-ref-thumb-p" : "omni-ref-thumb";
  const removePrefix = testIdSuffix === "pipeline" ? "omni-ref-remove-p" : "omni-ref-remove";

  const description =
    mode === "turnaround"
      ? "Upload multiple angles or outfit refs — every view locks identity from all of them."
      : mode === "text-video"
        ? "Upload refs to lock identity — a first frame is composed from them, then Seedance breathes it."
        : mode === "video-to-video"
          ? "Upload identity refs — their look is described to Seedance to guide the restyle."
          : mode === "pipeline"
            ? "Upload extra images — they'll influence every edit in the pipeline (identity, outfit, mood, backdrop)."
            : "Upload up to 6 images or videos — Nano Banana blends them all as identity, outfit, mood, and backdrop.";

  const acceptFile = (f) => f && (f.type.startsWith("image/") || f.type.startsWith("video/"));

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []).filter(acceptFile);
    const remaining = Math.max(0, 6 - omniRefs.length);
    files.slice(0, remaining).forEach((f) => addOmniRef(f));
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  return (
    <div
      className={`p-6 rounded-2xl bg-[#120E15] border transition-all ${dragOver ? "border-[#F2C94C] shadow-[0_0_24px_-6px_rgba(242,201,76,0.6)]" : "border-[#00E5FF]/20"}`}
      data-testid={`omni-ref-card${testIdSuffix ? "-" + testIdSuffix : ""}`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
    >
      <div className="flex items-center justify-between mb-2">
        <label className="font-mono-hy text-[10px] text-[#00E5FF] flex items-center gap-1.5">
          <LayoutGrid className="w-3 h-3" /> Omni Reference · {omniRefs.length}/6
        </label>
        {omniRefs.length > 0 && (
          <button
            onClick={() => setOmniRefs([])}
            data-testid="omni-clear-all-btn"
            className="font-mono-hy text-[9px] text-[#E27D60] hover:text-white"
          >Clear all</button>
        )}
      </div>
      <p className="font-mono-hy text-[9px] text-white mb-3">{description}</p>
      <input
        id={inputId}
        type="file"
        accept="image/*,video/*"
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          files.forEach((f) => addOmniRef(f));
          e.target.value = "";
        }}
        className="hidden"
        data-testid={inputId}
      />
      {omniRefs.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid={gridId}>
          {omniRefs.map((r, i) => (
            <div key={r.id || r.url || i} className="relative aspect-square rounded-lg overflow-hidden border border-[#00E5FF]/30 group" data-testid={`${thumbPrefix}-${i}`}>
              {r.kind === "video" ? (
                <video src={r.url} className="w-full h-full object-cover" muted playsInline />
              ) : (
                <img src={r.url} alt={`ref ${i}`} className="w-full h-full object-cover" />
              )}
              {!r.uploaded_url && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0A0A0A]/60">
                  <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />
                </div>
              )}
              <button
                onClick={() => removeOmniRef(i)}
                data-testid={`${removePrefix}-${i}`}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#0A0A0A]/80 border border-[#E27D60]/40 text-[#E27D60] hover:text-white hover:bg-[#E27D60] flex items-center justify-center transition-all"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => document.getElementById(inputId)?.click()}
        disabled={omniRefs.length >= 6}
        data-testid={pickBtnId}
        data-testid-alt={dropZoneId}
        className={`w-full h-20 rounded-xl border-2 border-dashed transition-all flex flex-col items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${
          dragOver
            ? "border-[#F2C94C] bg-[#F2C94C]/10 text-[#F2C94C]"
            : "border-[#00E5FF]/25 hover:border-[#00E5FF] hover:bg-[#00E5FF]/5 text-white"
        }`}
      >
        <Upload className={`w-4 h-4 ${dragOver ? "text-[#F2C94C]" : "text-[#00E5FF]/70"}`} />
        <span className="font-mono-hy text-[10px]">
          {dragOver
            ? "Drop to add references"
            : omniRefs.length === 0
              ? "Drag & drop or click · images and videos"
              : `Add more · ${6 - omniRefs.length} slots left`}
        </span>
      </button>
    </div>
  );
}
